import {
    Box,
    Stack,
    type SxProps,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    type Theme,
    Typography,
} from '@mui/material';
import InfoBanner from '/@/components/bayer/info-banner';
import Spinner from '/@/components/spinner';
import useUnitProvider from '/@/core/context/unit';
import { _, getActiveLanguage } from '/@/locale';
import Section from '/@/views/recommendations/v2/section';
import type { ManagementHybrid } from './use-management-data';

/** The endpoint returns the full set (ca, mg, b, cl, …); the design surfaces only these four. */
type NutrientKey = 'nitrogen' | 'phosphorus' | 'potassium' | 'sulfur';
const NUTRIENTS: { key: NutrientKey; header: string; highlight?: boolean }[] = [
    { key: 'nitrogen', header: 'N', highlight: true },
    { key: 'phosphorus', header: 'P' },
    { key: 'potassium', header: 'K' },
    { key: 'sulfur', header: 'S' },
];

/** Six equal columns, and a floor below which the figures stop fitting and the table scrolls. */
const MIN_TABLE_WIDTH = 614;
const COLUMN_WIDTH = `${100 / (NUTRIENTS.length + 2)}%`;

/** Nutrients are kg/ha either way, so the table prints the bare figure, as the design does. */
const formatNutrient = (value?: number) => {
    if (typeof value !== 'number' || Number.isNaN(value)) return '-';
    return `${value.toLocaleString(getActiveLanguage(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg/ha`;
};

const cellSx = ({ highlight, pinned }: { highlight?: boolean; pinned?: boolean } = {}): SxProps<Theme> => ({
    width: COLUMN_WIDTH,
    px: 1.5,
    py: 1,
    backgroundColor: (theme: Theme) =>
        highlight ? theme.palette.grey[100] : pinned ? theme.palette.background.paper : 'transparent',
    // The hybrid names stay put while the rest scrolls out from under them.
    ...(pinned
        ? {
              position: 'sticky',
              left: 0,
              zIndex: 1,
              // Not `borderRight`: the table collapses its borders, and a collapsed border on a
              // sticky cell stops painting once the row scrolls underneath it. An inset shadow
              // is drawn by the cell itself, so the separator survives the scroll.
              boxShadow: (theme: Theme) => `inset -1px 0 0 ${theme.palette.divider}`,
          }
        : {}),
});

/** The bare table, so the result page can show it without the tab's heading and banner. */
export function NutrientTable({ hybrids }: { hybrids: ManagementHybrid[] }) {
    const units = useUnitProvider();

    return (
        <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: MIN_TABLE_WIDTH, border: '1px solid', borderColor: 'divider' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={cellSx({ pinned: true })}>
                            <Typography variant='caption' fontWeight={700} color='primary'>
                                {_('Híbrido')}
                            </Typography>
                        </TableCell>
                        <TableCell sx={cellSx()}>
                            <Typography variant='caption' fontWeight={700} color='primary'>
                                {_('Rendimiento')}
                            </Typography>
                        </TableCell>
                        {NUTRIENTS.map((nutrient) => (
                            <TableCell key={nutrient.key} sx={cellSx({ highlight: nutrient.highlight })}>
                                <Typography variant='caption' fontWeight={700} color='primary'>
                                    {nutrient.header}
                                </Typography>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {hybrids.map((hybrid) => {
                        const estimatedYield =
                            hybrid.estimatedYield !== undefined
                                ? units.toCornYield(hybrid.estimatedYield, 0)
                                : undefined;
                        return (
                            <TableRow key={hybrid.id}>
                                <TableCell sx={cellSx({ pinned: true })}>
                                    <Typography variant='subtitle1' fontWeight={700} color='primary'>
                                        {hybrid.catalogName}
                                    </Typography>
                                </TableCell>
                                <TableCell sx={cellSx()}>
                                    <Typography variant='body2' color='primary'>
                                        {estimatedYield ? `${estimatedYield.localeValue} ${estimatedYield.unit}` : '-'}
                                    </Typography>
                                </TableCell>
                                {NUTRIENTS.map((nutrient) => (
                                    <TableCell key={nutrient.key} sx={cellSx({ highlight: nutrient.highlight })}>
                                        <Typography
                                            variant='body2'
                                            // Emphasised only for the hybrid-specific figure;
                                            // the generic fallback reads like any other nutrient.
                                            fontWeight={
                                                nutrient.key === 'nitrogen' && hybrid.nitrogenIsRecommended
                                                    ? 700
                                                    : undefined
                                            }
                                            color='primary'
                                        >
                                            {formatNutrient(hybrid[nutrient.key])}
                                        </Typography>
                                    </TableCell>
                                ))}
                            </TableRow>
                        );
                    })}
                </TableBody>
            </Table>
        </Box>
    );
}

/**
 * "Fertilización" tab: what the selected hybrids will take out of the soil to reach their
 * estimated yield. The figures are server-calculated per hybrid; the tab only formats them.
 */
function FertilizationTab({
    hybrids,
    isLoading,
    onCopy,
    copying,
}: {
    hybrids: ManagementHybrid[];
    isLoading?: boolean;
    /** Renders the "copy as image" button on the nutrient table's section when set. */
    onCopy?: () => void;
    copying?: boolean;
}) {
    if (isLoading && !hybrids.length) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    return (
        <Stack spacing={3}>
            <Section
                title={_('Requerimiento nutricional')}
                subtitle={_(
                    'Nutrientes totales que necesitan los híbridos seleccionados para el rendimiento estimado.'
                )}
                block={onCopy ? 'nutrients' : undefined}
                onCopy={onCopy}
                copying={copying}
            >
                <NutrientTable hybrids={hybrids} />
            </Section>

            <InfoBanner color='info'>
                <span>
                    <strong>{_('Sumá una recomendación de N-Smart:')}</strong>{' '}
                    {_(
                        'Sugerile al productor refertilizar previo a V6. La recomendación de Nitrógeno Smart la arma el equipo técnico y define la dosis según las condiciones del lote.'
                    )}
                </span>
            </InfoBanner>
        </Stack>
    );
}

export default FertilizationTab;
